"use client";

import React, { createContext, useContext, useState, useEffect, type ReactNode, useCallback } from 'react';
import { type UserInfo } from '~/lib/objects';

// Define the structure for the context value
interface UserInfoContextValue {
    userInfo?: UserInfo;
    refetchUserInfo: () => void;
}

// Create the context with a default value (or undefined)
const UserInfoContext = createContext<UserInfoContextValue | undefined>(undefined);

// Define the props for the provider component
interface UserInfoProviderProps {
    children: ReactNode;
}

// Create the provider component
export const UserInfoProvider: React.FC<UserInfoProviderProps> = ({ children }) => {
    const [userInfo, setUserInfo] = useState<UserInfo | undefined>(undefined);

    // Define the function to fetch user data
    const fetchUserData = useCallback(async () => {
        console.log("Fetching user info..."); // Placeholder log
        try {
            // Replace with your actual API call
            const response = await fetch('/api/user-info');
            const data = await response.json() as UserInfo;
            if (response.ok)
                setUserInfo(data);
        } catch (error) {
            console.error("Failed to fetch user info:", error);
        }
    }, []);

    useEffect(() => {
        /* eslint-disable-next-line */
        fetchUserData(); // Fetch data on initial mount
    }, [fetchUserData]); // Re-run if fetchUserData changes (due to dependencies)

    // The value provided by the context
    const contextValue = {
        userInfo,
        refetchUserInfo: fetchUserData, // Expose the fetch function
    };

    return (
        <UserInfoContext.Provider value={contextValue}>
            {children}
        </UserInfoContext.Provider>
    );
};

// Create a custom hook to use the UserInfo context
export const useUserInfo = (): UserInfoContextValue => {
    const context = useContext(UserInfoContext);
    if (context === undefined) {
        throw new Error('useUserInfo must be used within a UserInfoProvider');
    }
    return context;
};
